import cx from 'classnames';
import {FC, memo, RefObject, useCallback, useMemo, useRef} from 'react';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {AssetIconType, EffectAssetIconType} from '~/common/types/assets_icons';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {Video} from '~library/atoms/Video';
import {VideoAlphaChannel} from '~library/atoms/VideoAlphaChannel';
import {SparkleIcon} from '~library/svg/SparkleIcon';

interface EditorIconProps {
  icon: AssetIconType;
  height?: number;
  width?: number;
  onClick?: (icon: AssetIconType, id: string | number) => void;
  isActive?: boolean;
  id?: string | number;
  hideAnimatedIndicator?: boolean;
  flipEnvelope?: (isFrontSide: boolean) => void;
  showEnvelopeFront?: boolean | null;
  circle?: boolean;
  loading?: 'lazy' | 'eager';
  outline?: boolean;
  cssCover?: boolean;
}

const getSrcFromIcon = (icon: AssetIconType): string => {
  if (typeof icon === 'string') {
    return icon;
  }

  if (typeof icon === 'object' && 'url' in icon) {
    return icon.url;
  }

  return icon.defaultSrc;
};

const isEffectIcon = (icon: AssetIconType): icon is EffectAssetIconType =>
  typeof icon === 'object' && 'iconVp9' in icon && 'iconH265' in icon;

export const EditorIcon: FC<EditorIconProps> = memo(
  ({
    icon,
    height = 32,
    width = 32,
    onClick,
    isActive,
    id = '',
    hideAnimatedIndicator = false,
    flipEnvelope,
    showEnvelopeFront = null,
    circle = false,
    loading = 'lazy',
    outline,
    cssCover,
  }) => {
    const isLargishAndUp = useIsLargishAndUp();
    const borderRadius = circle ? '50%' : '0.25rem';
    const loadingRef = useRef<HTMLDivElement | null>(null);
    const iconRef = useRef<HTMLImageElement | HTMLVideoElement | HTMLDivElement | null>(null);

    const loadedRef = useRef(false);
    const isNone = icon === '' || (typeof icon === 'object' && !icon.id);

    const defaultVideoSrc = getSrcFromIcon(icon);
    const isVideo = isAllowedVideoFormat(defaultVideoSrc || '');
    const dataQaId = isVideo ? 'icon-unit-video-selector' : 'icon-unit-img-selector';

    const className = cx('editor-icon', {
      'editor-icon--hidden': !loadedRef.current && !isNone,
      'editor-icon--outline': outline,
      'editor-icon--cover': cssCover,
    });

    const onClickHandler = useCallback(() => {
      if (!isActive && onClick) {
        if (flipEnvelope) {
          flipEnvelope(!!showEnvelopeFront);
        }
        onClick(icon, id);
      }
    }, [isActive, icon, id, onClick, flipEnvelope, showEnvelopeFront]);

    const finishedLoading = useCallback(() => {
      loadedRef.current = true;
      if (loadingRef.current)
        if ('style' in loadingRef.current) {
          loadingRef.current.style.visibility = 'hidden';
        }
      if (iconRef?.current)
        if ('classList' in iconRef.current) {
          iconRef.current.classList.remove('editor-icon--hidden');
        }
    }, []);

    const style = useMemo(
      () => ({
        height,
        minWidth: width,
        width,
      }),
      [height, width]
    );

    const videoIcon = useMemo(() => {
      if (typeof icon === 'string') {
        return (
          <Video
            ref={iconRef as RefObject<HTMLVideoElement>}
            className={className}
            style={{...style, objectFit: 'fill'}}
            onCanPlay={finishedLoading}
            src={icon}
            inViewAutoPlay
          />
        );
      }

      return (
        isEffectIcon(icon) && (
          <VideoAlphaChannel
            ref={iconRef as RefObject<HTMLVideoElement>}
            className={className}
            style={{...style, objectFit: 'fill'}}
            onCanPlay={finishedLoading}
            srcVp9={icon.iconVp9}
            srcH265={icon.iconH265}
            inViewAutoPlay
          />
        )
      );
    }, [icon, className, style, finishedLoading]);

    const imageIcon = useMemo(() => {
      if (isNone) {
        return (
          <div
            ref={iconRef as RefObject<HTMLDivElement>}
            className={cx('editor-icon--none', className)}
            style={style}
          >
            <Typography variant="paragraph4" color="charcoal">
              NONE
            </Typography>
          </div>
        );
      }

      return (
        <img
          loading={loading as 'lazy' | 'eager'}
          ref={iconRef as RefObject<HTMLImageElement>}
          className={className}
          src={getSrcFromIcon(icon)}
          onLoad={finishedLoading}
          alt="Icon"
        />
      );
    }, [icon, className, style, finishedLoading, isNone, loading]);

    return (
      <div className="editor-icon__wrapper" style={style}>
        <div
          style={{height, width, visibility: loadedRef.current ? 'hidden' : 'visible'}}
          className="editor-icon editor-icon--loader"
          ref={loadingRef}
        >
          <span className="evt-spinner inline" />
        </div>
        <Button
          className="editor-icon__button"
          variant="transparent"
          data-qa-id={dataQaId}
          onClick={onClickHandler}
          rounded={circle}
        >
          {isVideo ? videoIcon : imageIcon}
        </Button>
        {isActive && <div className="editor-icon__active-border" style={{borderRadius}} />}
        {isVideo && !hideAnimatedIndicator && (
          <SparkleIcon
            ratio={isLargishAndUp ? 1 : 0.75}
            className="editor-icon__animated-indicator"
          />
        )}
      </div>
    );
  }
);
